import { Button as BaseButton } from '@base-ui/react/button';
import type { ComponentProps } from 'react';
import { cn } from '../../lib/cn';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'destructive' | 'link';
type Size = 'sm' | 'md' | 'lg';

const VARIANTS: Record<Variant, string> = {
  // Залитое действие — чернильное `control` (палитра прототипа), синий — только для ссылок и фокуса.
  primary:
    'bg-control text-control-foreground hover:bg-control-hover data-[disabled]:bg-control-disabled',
  // `.btn-ghost` прототипа: вторичный текст и рамка, при наведении — темнее.
  secondary:
    'border border-border bg-transparent text-muted-foreground hover:border-border-hover hover:text-foreground data-[disabled]:text-hint',
  ghost: 'text-foreground hover:bg-tint data-[disabled]:text-hint',
  danger: 'border border-danger text-danger hover:bg-danger-soft data-[disabled]:opacity-50',
  // Подтверждение разрушительного действия: залитая тёмно-красная (`.btn-danger` прототипа).
  destructive:
    'bg-danger-dark text-control-foreground hover:bg-danger-hover data-[disabled]:opacity-50',
  link: 'h-auto px-0 text-primary underline-offset-2 hover:text-primary-hover hover:underline',
};

const SIZES: Record<Size, string> = {
  sm: 'h-8 px-3 text-sm',
  md: 'h-10 px-5 text-sm',
  lg: 'h-11 px-6 text-sm',
};

const BASE =
  'inline-flex shrink-0 items-center justify-center gap-2 rounded-sm font-medium whitespace-nowrap no-underline transition-colors outline-none focus-visible:shadow-focus focus-visible:ring-1 focus-visible:ring-primary data-[disabled]:cursor-not-allowed [&_svg]:size-4 [&_svg]:shrink-0';

/** Классы кнопки — и для ссылки, которая выглядит кнопкой (навигация остаётся ссылкой). */
export function buttonClassName({
  variant = 'primary',
  size = 'md',
  className,
}: { variant?: Variant; size?: Size; className?: string | undefined } = {}): string {
  return cn(BASE, SIZES[size], VARIANTS[variant], className);
}

export interface ButtonProps extends Omit<ComponentProps<typeof BaseButton>, 'className'> {
  variant?: Variant;
  size?: Size;
  className?: string;
}

export function Button({ variant = 'primary', size = 'md', className, ...props }: ButtonProps) {
  return <BaseButton className={buttonClassName({ variant, size, className })} {...props} />;
}
